import { Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";
import { electronModule } from "@/modules/electron";
import { uuidModule } from "@/modules/uuid";
import {
  PreparedRecording,
  PreparedRecordingController,
  RecallUploadCredentials,
} from "@/store/audio/recording/prepared-recording-controller/types";
import { DateTime } from "luxon";

interface RecallSdkUploadResponse {
  upload_token: string;
  sdk_upload_id?: string;
}

export class DesktopPreparedRecordingController implements PreparedRecordingController {
  prepareRecording({
    customVocabulary,
    audioRecordingId,
  }: {
    customVocabulary: string[] | null;
    audioRecordingId: Uuid;
  }): PreparedRecording {
    return {
      audioRecordingId,
      customVocabulary,
      windowId: this.fetchWindowId(),
      recallUploadCredentials: this.fetchRecallUploadCredentials(
        audioRecordingId,
        customVocabulary
      ),
      // After 3 hours, @recallai/desktop-sdk startRecording needs to do a network
      // request to confirm it, taking 0.5~1 second.
      expiresAt: DateTime.local().plus({ hours: 2, minutes: 30 }),
      recallStarted: false,
    };
  }

  private async fetchWindowId(): Promise<string> {
    const { windowId } = await electronModule.recall.prepareDesktopRecording();
    return windowId;
  }

  private async fetchRecallUploadCredentials(
    audioRecordingId: Uuid,
    customVocabulary: string[] | null
  ): Promise<RecallUploadCredentials> {
    const capturedAudioId = uuidModule.resolveAudioRecordingCapturedAudioUuid({
      associatedAudioRecordingId: audioRecordingId,
    });
    const response = await api.post("/v2/voice/recall_sdk_upload", {
      body: {
        // This is not being used by the backend.
        note_id: "00000000-0000-0000-0000-000000000000",
        captured_audio_id: capturedAudioId,
        recording_config: {
          transcript: {
            provider: {
              assembly_ai_v3_streaming: {
                format_turns: true,

                keyterms_prompt: customVocabulary?.length ? JSON.stringify(customVocabulary) : null,
              },
            },
          },
          realtime_endpoints: [
            {
              type: "desktop-sdk-callback",
              events: [
                "participant_events.join",
                "transcript.data",
                "transcript.partial_data",
                "audio_mixed_raw.data",
              ],
            },
          ],
          // System audio only
          video_mixed_mp4: null,
          audio_mixed_mp3: {},
        },
      },
    });
    const data = response.data as RecallSdkUploadResponse | undefined;
    if (!data?.upload_token) throw new Error("Failed to get desktop capture upload token");
    return {
      uploadToken: data.upload_token,
      sdkUploadId: data.sdk_upload_id ?? null,
    };
  }
}
